import { useEffect, useContext, useState } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/common-hooks';
import { PERMISSION_STATUS } from '@bingads-webui-campaign-react/linkedin-utils';
import {
  getCompanyPagePermissionStatus,
  getConnectedLinkedInCompanyPage,
} from '@bingads-webui-campaign-react/linkedin-data';

export const useInitializeCompanyPage = () => {
  const { scope } = useContext(ScopeContext);
  const { cid, aid } = scope.values;
  const logger = useInstrumentLogger({
    scenarioName: 'LinkedInWizardInitialization',
    activityName: 'InitializeCompanyPage',
  });

  const [isCompanyPageInitialized, setIsCompanyPageInitialized] = useState(false);
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [isMemberTokenActive, setIsMemberTokenActive] = useState(true);
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [connectedCompanyPage, setConnectedCompanyPage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const isCompanyPageReady = isMemberTokenActive && connectedCompanyPage && permissionStatus === PERMISSION_STATUS.APPROVED;

  useEffect(() => {
    if (loadError) {
      // Throw error here because error boundaries don't catch errors thrown on async code https://reactjs.org/docs/error-boundaries.html
      // Need to show lady page because it is a blocking error
      throw new Error(loadError);
    }
  }, [loadError]);

  useEffect(() => {
    if (!isCompanyPageInitialized && !isLoading && !loadError) {
      setIsLoading(true);
      getConnectedLinkedInCompanyPage(cid, aid)
        .then((res) => {
          if (res && res.Id) {
            setConnectedCompanyPage(res);
            setIsFirstRun(false);
            if (res.IsMemberTokenActive === false) {
              setIsMemberTokenActive(false);
            }
            return getCompanyPagePermissionStatus(cid, aid);
          }
          return Promise.resolve();
        })
        .then((res) => {
          if (res && res.value) {
            setPermissionStatus(res.value);
          }
          setIsCompanyPageInitialized(true);
        })
        .catch((err) => {
          logger.trace(`Failed to initialize company page: ${JSON.stringify(err)}`);
          setLoadError(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [aid, cid, isCompanyPageInitialized, isLoading, loadError, logger]);

  return {
    isCompanyPageInitialized,
    isCompanyPageReady,
    isFirstRun,
    isMemberTokenActive,
    permissionStatus,
    connectedCompanyPage,
  };
};
